// views/cream-table-model.js: restyle step 2, the words and numbers the
// cream table shows, from a server snapshot. Pure, no DOM.
//
// Slots name the four places at the table from the viewer's side: S is
// you, W your left, N your partner, E your right (cream-layout.js).

import { lonerSeat } from '../canvas/table.js';
import { passedSeats } from '../canvas/bid-bubbles.js';
import { seatsBySlot, textWidth, shortName } from '../canvas/cream-layout.js';
import { dealerPickupCaption } from '../canvas/upcard.js';

const LIVE = new Set(['dealer_discard', 'trick_play']);
export const SUIT_WORD = { S: 'SPADES', H: 'HEARTS', D: 'DIAMONDS', C: 'CLUBS' };
export const SUIT_SYMBOL = { S: '♠', H: '♥', D: '♦', C: '♣' };

/** Lua tables can arrive as keyed objects. */
export function asList(v) {
  if (!v) return [];
  return Array.isArray(v) ? v : Object.values(v);
}

/** team_a holds seats 1 and 3, team_b seats 2 and 4. */
export function teamOf(seat) {
  return seat === 1 || seat === 3 ? 'team_a' : 'team_b';
}

/** Cards each seat has played this hand (finished tricks and the trick on the table). */
function playedBySeat(snap) {
  /** @type {Record<number, number>} */
  const n = { 1: 0, 2: 0, 3: 0, 4: 0 };
  for (const t of asList(snap?.completed_tricks)) for (const p of asList(t?.plays)) n[Number(p.seat)] += 1;
  for (const p of asList(snap?.trick_plays)) n[Number(p.seat)] += 1;
  return n;
}

/**
 * What each seat tag says.
 *
 * @param {any} snap
 * @param {number} viewerSeat  lua 1-4
 * @param {Record<number, string>} labels  seat names (with the "· bot" mark)
 * @returns {Record<'S'|'W'|'N'|'E', import('../canvas/cream-layout.js').TagContent & {seat:number, fullName:string}>}
 */
export function tagContents(snap, viewerSeat, labels) {
  const bySlot = seatsBySlot(viewerSeat);
  const played = playedBySeat(snap);
  const loner = lonerSeat(snap);
  const cs = snap?.current_state ?? '';
  const sittingOut = LIVE.has(cs) ? snap?.sitting_out_seat ?? null : null;
  /** @type {any} */
  const out = {};
  for (const slot of /** @type {const} */ (['S', 'W', 'N', 'E'])) {
    const seat = bySlot[slot];
    let status = null;
    if (loner === seat) status = 'ALONE';
    else if (snap?.trump_suit && snap?.maker_seat === seat) status = 'CALLED';
    else if (sittingOut === seat) status = 'SITS OUT';
    const name = labels?.[seat] || `Seat ${seat}`;
    out[slot] = {
      seat,
      name: slot === 'S' ? 'YOU' : name,
      fullName: slot === 'S' ? 'You' : name,
      you: slot === 'S',
      partner: slot === 'N',
      status,
      count: slot === 'S' || sittingOut === seat ? null : Math.max(0, 5 - played[seat]),
    };
  }
  return out;
}

/**
 * "BOT ATOM'S TURN" style text for the turn chip; while a trick clears,
 * who takes it.
 *
 * @param {any} snap
 * @param {number} viewerSeat
 * @param {Record<number, string>|null|undefined} plainLabels
 * @param {number|null} [takerSeat] the trick's winner while it clears
 * @returns {string}
 */
export function turnChipText(snap, viewerSeat, plainLabels, takerSeat = null) {
  const cs = snap?.current_state ?? '';
  if (!snap) return '';
  const name = (s) => (plainLabels?.[s] || `Seat ${s}`).toUpperCase();
  // J5: from a trick's 4th card until its clear ends, who takes it (the
  // mockup's turnText), also for the last trick of a hand.
  if (takerSeat != null) return takerSeat === viewerSeat ? 'YOU TAKE IT' : `${name(takerSeat)} TAKES IT`;
  if (cs === 'end_of_hand' || cs === 'score') return 'HAND OVER';
  const seat = snap.current_turn_seat;
  if (cs === 'dealer_discard') {
    const dealer = snap.dealer_seat;
    return dealer === viewerSeat ? 'YOUR DISCARD' : `${name(dealer)} DISCARDS`;
  }
  if (seat == null) return '';
  return seat === viewerSeat ? 'YOUR TURN' : `${name(seat)}'S TURN`;
}

/**
 * The controls row's short line (C6): "TAP A CARD" on your trick turn,
 * "WAITING FOR <NAME>" while another seat acts, the dealer's pickup.
 */
export function hintText(snap, viewerSeat, plainLabels) {
  if (!snap) return '';
  const cs = snap.current_state ?? '';
  const seat = snap.current_turn_seat;
  const name = (s) => (plainLabels?.[s] || `Seat ${s}`).toUpperCase();
  if (cs === 'dealer_discard') {
    if (snap.dealer_seat === viewerSeat) return '';
    return dealerPickupCaption(snap, viewerSeat, plainLabels).toUpperCase();
  }
  if (cs === 'trick_play' || cs === 'bid_r1' || cs === 'bid_r2') {
    if (seat == null) return '';
    if (seat === viewerSeat) return cs === 'trick_play' ? 'TAP A CARD' : '';
    return `WAITING FOR ${name(seat)}`;
  }
  return '';
}

/** Score blocks: game points and tricks this hand, for US (your team) and THEM. */
export function scoreModel(snap, viewerSeat) {
  const us = teamOf(viewerSeat);
  const them = us === 'team_a' ? 'team_b' : 'team_a';
  const scores = snap?.scores || {};
  const tricks = snap?.trick_counts || {};
  const target = Number.isFinite(snap?.game_target) && snap.game_target > 0 ? snap.game_target : 10;
  return {
    target,
    US: { team: us, score: Number(scores[us] ?? 0), tricks: Number(tricks[us] ?? 0) },
    THEM: { team: them, score: Number(scores[them] ?? 0), tricks: Number(tricks[them] ?? 0) },
  };
}

/** "HAND n", from the 0-based hand index. */
export function handNumber(snap) {
  const i = Number.isFinite(snap?.hand_index) && snap.hand_index >= 0 ? Math.floor(snap.hand_index) : 0;
  return i + 1;
}

/**
 * The last finished trick of this hand, for the last-trick view: its
 * cards (in play order), who led and who won.
 */
export function lastTrick(snap) {
  const done = asList(snap?.completed_tricks);
  const t = done[done.length - 1];
  if (!t) return null;
  const plays = asList(t.plays).map((p) => ({ seat: Number(p.seat), card_id: String(p.card_id) }));
  if (!plays.length) return null;
  return { plays, leader: plays[0].seat, winner: Number(t.winner_seat) };
}

/**
 * The name an empty trick cell shows (never empty): the whole name when
 * it fits the cell, else the mockup's short form (six letters and a
 * dot), else as many letters as fit, and a dot. A second line says PASS
 * for a seat that passed this bid round.
 *
 * @param {string} name
 * @param {number} cellW
 * @param {boolean} passed
 */
export function cellLabel(name, cellW, passed) {
  const room = cellW - 8;
  const up = name.toUpperCase();
  let text = up;
  if (textWidth(up, 16) > room) {
    text = shortName(up);
    const chars = [...up];
    for (let n = Math.min(6, chars.length - 1); n >= 1 && textWidth(text, 16) > room; n -= 1) text = `${chars.slice(0, n).join('').trimEnd()}.`;
  }
  return { text, pass: passed };
}

export { passedSeats };
